import { Fishx, i18n } from 'fishx';
import memoizeOne from 'memoize-one';
import { message } from '@whalecloud/fdx';
import isEqual from 'lodash/isEqual';
import includes from 'lodash/includes';
import {
  queryMenus,
  queryFavoriteMenu,
  addFavoriteMenu,
  queryPortalsByUser,
  setCurrentPortal,
  setFavorites,
  deleteFavorites,
  qryCurrentUserSpList,
  setCurrentSp,
} from '@/services/user';
import { menuAdapter, setCookie, getWebRoot } from '@/utils/utils';
import loginSession from '@/utils/loginSession';
import appGlobal from '@/utils/appGlobal';
import { Effect, Reducer } from '@/typings/connect.d';
import { RawMenuType } from '@/typings/data.d';

export interface Menu {
  children?: Menu[];
  path: string;
  type: string;
}

/**
 * 获取面包屑映射
 * @param {Object} menuData 菜单配置
 */
const getBreadcrumbNameMap = (menuData: Menu[]) => {
  const routerMap = {};
  let path = '';
  const flattenMenuData = (data: Menu[]) => {
    data.forEach((menuItem, index) => {
      if (menuItem.children) {
        path += menuItem.path;
        flattenMenuData(menuItem.children);
      }
      // Reduce memory usage
      routerMap[`${path}${menuItem.path}`] = menuItem;
      if (index === data.length - 1 && !menuItem.children && menuItem.type === '1') {
        path = '';
      }
    });
  };
  flattenMenuData(menuData);
  return routerMap;
};
const memoizeOneGetBreadcrumbNameMap = memoizeOne(getBreadcrumbNameMap, isEqual);

// TODO
export interface UserStateType {
  list: [];
  currentUser: {};
  menu: [];
  rawMenu: RawMenuType[];
  breadcrumbNameMap: {};
  portals: [];
  menuLoaded: boolean;
  favorites: [];
  spList: [];
}

export interface UserModel {
  namespace: string;
  state: UserStateType;
  effects: {
    addFavoriteMenu: Effect;
    fetchMenu: Effect;
    fetchDefaultMenu: Effect;
    fetchPortals: Effect;
    setCurrentPortal: Effect;
    setFavorite: Effect;
    delFavorite: Effect;
    fetchCurrentUserSpList: Effect;
    setCurrentSp: Effect;
  };
  reducers: {
    saveMenu: Reducer<UserStateType>;
    save: Reducer<Partial<UserStateType>>;
    saveCurrentUser: Reducer<Partial<UserStateType>>;
    changeNotifyCount: Reducer<Partial<UserStateType>>;
    saveFavorites: Reducer<UserStateType>;
  };
}

const userModel: UserModel = {
  namespace: 'dxpUser',

  state: {
    list: [],
    currentUser: {},
    menu: [],
    rawMenu: [],
    breadcrumbNameMap: {},
    portals: [],
    menuLoaded: false,
    favorites: [],
    spList: [],
  },

  effects: {
    *addFavoriteMenu(_, { call }) {
      yield call(addFavoriteMenu);
    },
    *fetchMenu({ payload = {} }, { call, put, select }) {
      const { currentUser } = yield select((state: any) => state.dxpGlobal);
      const { portalId } = currentUser;
      const { success, data } = yield call(queryMenus, portalId);
      if (success) {
        const menuData = menuAdapter(data, 'partyId', 'parentId', 'children');
        const breadcrumbNameMap = memoizeOneGetBreadcrumbNameMap(menuData);
        yield put({
          type: 'saveMenu',
          payload: {
            menu: menuData,
            rawMenu: data,
            breadcrumbNameMap,
            menuLoaded: true,
          },
        });
        const { from = '' } = payload;
        if (from === 'Login') {
          const { redirect = '/' } = payload;
          if (redirect && redirect.indexOf('/userLogin') <= -1) {
            // 判断redirect是否存在于menuList
            if (data.some((v: any) => includes(redirect, v.url))) {
              // window.location.href = redirect;
              Fishx.history.push(redirect);
            } else {
              yield put({
                type: 'fetchDefaultMenu',
                payload: {
                  from,
                },
              });
            }
          } else if (window.location.hash.indexOf('/userLogin') <= -1 || window.location.pathname.indexOf('/userLogin') <= -1) {
            // 路由兼容处理
            yield put({
              type: 'fetchDefaultMenu',
              payload: {
                from,
              },
            });
          }
        }
      }
    },
    
    *fetchDefaultMenu({ payload = {} }, { call, put, select }) {
      const { currentUser } = yield select((state: any) => state.dxpGlobal);
      const { portalId } = currentUser;
      const { success, data } = yield call(queryFavoriteMenu, portalId);
      if (success) {
        const defaultMenu = data.find((v: any) => v.isDefaultOpen === 'Y');
        let defaultUrl = defaultMenu ? defaultMenu.url : '/';
        defaultUrl = defaultUrl.indexOf('/') > -1 ? defaultUrl : `/${defaultUrl}`;
        appGlobal.setDefaultUrl(defaultUrl);
        if (payload.from) {
        // if (payload.from || loginSession.getData('changePortal') === true) {
          Fishx.history.push(defaultUrl);
          // loginSession.saveData('changePortal', false);
        }

        // TODO 下面的代码是在哪个场景下使用的？
        if (payload.getData) {
          const newData = JSON.parse(JSON.stringify(data));
          newData.filter((item: any, index: number) => {
            Object.assign(item, { key: `favorite-${index}` });
            return item;
          });
          yield put({
            type: 'saveFavorites',
            payload: {
              favorites: newData,
            },
          });
        }
      }
    },

    *fetchPortals(_, { call, put, select }) {
      const {
        currentUser: { userId },
      } = yield select((state: any) => state.dxpGlobal);
      const { success, data } = yield call(queryPortalsByUser, userId);
      if (success) {
        yield put({
          type: 'saveMenu',
          payload: { portals: data },
        });
      }
    },
    *setCurrentPortal({ payload }, { call }) {
      const { success, data } = yield call(setCurrentPortal, payload.portalId);
      if (success && data === 'success') {
        loginSession.saveData('changePortal', true);
        setCookie('INIT_PORTAL', payload.portalId, 30);
        if (payload.portalUrl) {
          const { origin } = window.top.location;
          window.top.location.href = `${origin}${getWebRoot()}/${payload.portalUrl}`;
        } else {
          Fishx.history.push('/');
          window.location.reload();
        }
      }
    },
    *setFavorite({ payload }, { call, put }) {
      const { param, method } = payload;
      const response = yield call(setFavorites, param, method);
      if (response.success) {
        yield put({
          type: 'fetchDefaultMenu',
          payload: {
            getData: true,
          },
        });
        message.success(i18n('SUCCESS_FAVORITE_NEMU'));
      } else if (response === 'success') {
        yield put({
          type: 'fetchDefaultMenu',
          payload: {
            getData: true,
          },
        });
        message.warning(i18n('FAILED_MODIFY_FAVORITE_NEMU'));
      } else {
        message.warning(method === 'POST' ? i18n('FAILED_FAVORITE_NEMU') : i18n('FAILED_MODIFY_FAVORITE_NEMU'));
      }
    },
    *delFavorite({ payload }, { call, put }) {
      const { menuFavId } = payload;
      const { success } = yield call(deleteFavorites, menuFavId);
      if (success) {
        yield put({
          type: 'fetchDefaultMenu',
          payload: {
            getData: true,
          },
        });
        message.success(i18n('SUCCEED_DELETE_FAVORITE_NEMU'));
      } else {
        message.warning(i18n('FAILED_DELETE_FAVORITE_NEMU'));
      }
    },
    *fetchCurrentUserSpList(_, { call, put }) {
      const { success, data } = yield call(qryCurrentUserSpList);
      if (success) {
        appGlobal.setSpId(data.CURRENT_SP);
        yield put({
          type: 'saveMenu',
          payload: { spList: data.SP_LIST },
        });
      }
    },
    *setCurrentSp({ payload }, { call }) {
      const { success } = yield call(setCurrentSp, payload);
      if (success) {
        Fishx.history.push('/');
        window.location.reload();
      }
    },
  },

  reducers: {
    saveMenu(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    save(state, action) {
      return {
        ...state,
        list: action.payload,
      };
    },
    saveCurrentUser(state, action) {
      return {
        ...state,
        currentUser: action.payload || {},
      };
    },
    changeNotifyCount(state, action) {
      return {
        ...state,
        currentUser: {
          ...state?.currentUser,
          notifyCount: action.payload,
        },
      };
    },
    saveFavorites(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
};

export default userModel;
